document.addEventListener('DOMContentLoaded', () => {
  initPreloader();
  initScrollProgress();
  initStickyHeader();
  initHeroSlider();
  initDrawers();
  initAccordions();
  initProductPage();
  initQuickAdd();
  initNativeCart();
  initCardReveal();
  initRevealElements();
  markActiveNavLink();
  initImageProtection();
  initZoomProtection();
  initPredictiveSearch();
  initSizeGuide();
});

/* ==========================================================================
   STICKY HEADER
   ========================================================================== */
function initStickyHeader() {
  const header = document.querySelector('.header-wrapper');
  if (!header) return;
  
  let lastScroll = 0;
  window.addEventListener('scroll', () => {
    const currentScroll = window.pageYOffset;
    if (currentScroll <= 0) {
      header.classList.remove('scroll-up');
      return;
    }
    
    if (currentScroll > lastScroll && !header.classList.contains('scroll-down')) {
      // scroll down
      header.classList.remove('scroll-up');
      header.classList.add('scroll-down');
    } else if (currentScroll < lastScroll && header.classList.contains('scroll-down')) {
      // scroll up
      header.classList.remove('scroll-down');
      header.classList.add('scroll-up');
    }
    lastScroll = currentScroll;
  });
}

/* ==========================================================================
   HERO SLIDER
   ========================================================================== */
function initHeroSlider() {
  const slider = document.querySelector('.hero-slider');
  if (!slider) return;
  
  const slides = slider.querySelectorAll('.hero-slide');
  const prevBtn = slider.querySelector('.slider-prev');
  const nextBtn = slider.querySelector('.slider-next');
  const dotsContainer = slider.querySelector('.slider-dots');
  
  if (slides.length <= 1) return;
  
  let currentIndex = 0;
  let autoplayInterval;
  
  // Create dots
  slides.forEach((_, index) => {
    const dot = document.createElement('button');
    dot.classList.add('slider-dot');
    if (index === 0) dot.classList.add('active');
    dot.setAttribute('aria-label', `Go to slide ${index + 1}`);
    dotsContainer.appendChild(dot);
    
    dot.addEventListener('click', () => {
      goToSlide(index);
      resetAutoplay();
    });
  });
  
  const dots = dotsContainer.querySelectorAll('.slider-dot');
  
  function goToSlide(index) {
    slides[currentIndex].classList.remove('active');
    dots[currentIndex].classList.remove('active');
    
    currentIndex = (index + slides.length) % slides.length;
    
    slides[currentIndex].classList.add('active');
    dots[currentIndex].classList.add('active');
  }
  
  function nextSlide() {
    goToSlide(currentIndex + 1);
  }
  
  function prevSlide() {
    goToSlide(currentIndex - 1);
  }
  
  if (prevBtn) prevBtn.addEventListener('click', () => { prevSlide(); resetAutoplay(); });
  if (nextBtn) nextBtn.addEventListener('click', () => { nextSlide(); resetAutoplay(); });
  
  function startAutoplay() {
    autoplayInterval = setInterval(nextSlide, 6000);
  }
  
  function resetAutoplay() {
    clearInterval(autoplayInterval);
    startAutoplay();
  }
  
  startAutoplay();
}

/* ==========================================================================
   DRAWERS (CART & SEARCH)
   ========================================================================== */
function initDrawers() {
  setupDrawer('cart-drawer', 'cart-open-btn', 'cart-close-btn');
  setupDrawer('search-drawer', 'search-open-btn', 'search-close-btn');
  setupDrawer('menu-drawer', 'menu-open-btn', 'menu-close-btn');
}

function setupDrawer(drawerId, openTriggerClass, closeTriggerClass) {
  const drawer = document.getElementById(drawerId);
  if (!drawer) return;
  
  const openButtons = document.querySelectorAll(`.${openTriggerClass}`);
  const closeButtons = drawer.querySelectorAll(`.${closeTriggerClass}`);
  const scroller = drawer.querySelector('.drawer-scroller');
  const sheet = drawer.querySelector('.drawer-sheet');
  
  if (!scroller || !sheet) return;

  // Timeline support detection & JS fallback
  if (!CSS.supports('animation-timeline: scroll()')) {
    scroller.addEventListener('scroll', () => {
      const ratio = 1 - scroller.scrollLeft / sheet.offsetWidth;
      drawer.style.setProperty('--drawer-backdrop', Math.max(0, Math.min(1, ratio)));
    });
  }

  async function openDrawer(e) {
    if (e) e.preventDefault();
    drawer.showPopover();
    
    if (!CSS.supports('scroll-initial-target', 'nearest')) {
      scroller.scrollTo({left: scroller.offsetWidth, behavior: 'instant'});
      await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
    }
    
    scroller.scrollTo({left: 0, behavior: 'auto'});
  }

  function closeDrawer() {
    scroller.scrollTo({left: scroller.offsetWidth, behavior: 'auto'});
  }

  function onDrawerOpened() {
    const main = document.querySelector('main');
    if (main) main.inert = true;
    openButtons.forEach(btn => btn.setAttribute('aria-expanded', 'true'));
    sheet.focus();
  }

  function onDrawerClosed() {
    drawer.hidePopover();
    const main = document.querySelector('main');
    if (main) main.inert = false;
    openButtons.forEach(btn => btn.setAttribute('aria-expanded', 'false'));
  }

  const visibleThreshold = 1 / window.innerWidth;
  const observer = new IntersectionObserver((entries) => {
    const entry = entries.at(-1);
    if (entry.intersectionRatio < visibleThreshold) onDrawerClosed();
    if (entry.intersectionRatio === 1) onDrawerOpened();
  }, {
    root: drawer,
    threshold: [visibleThreshold, 1]
  });

  observer.observe(sheet);

  openButtons.forEach(btn => btn.addEventListener('click', openDrawer));
  closeButtons.forEach(btn => btn.addEventListener('click', closeDrawer));

  // Light-dismiss click outside sheet
  drawer.addEventListener('click', (event) => {
    if (!sheet.contains(event.target)) closeDrawer();
  });

  // Esc key dismiss
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeDrawer();
  });
}

/* ==========================================================================
   PRODUCT ACCORDIONS
   ========================================================================== */
function initAccordions() {
  const accordions = document.querySelectorAll('.accordion-item');
  accordions.forEach(item => {
    const header = item.querySelector('.accordion-header');
    const content = item.querySelector('.accordion-content');
    
    if (!header || !content) return;
    
    header.addEventListener('click', () => {
      const isActive = item.classList.contains('active');
      
      // Close others
      accordions.forEach(other => {
        if (other !== item) {
          other.classList.remove('active');
          other.querySelector('.accordion-content').style.maxHeight = null;
        }
      });
      
      if (isActive) {
        item.classList.remove('active');
        content.style.maxHeight = null;
      } else {
        item.classList.add('active');
        content.style.maxHeight = content.scrollHeight + 'px';
      }
    });
  });
}

/* ==========================================================================
   PRODUCT DETAIL PAGE
   ========================================================================== */
function initProductPage() {
  const gallery = document.querySelector('.product-gallery');
  if (!gallery) return;
  
  const mainImage = gallery.querySelector('.product-main-image img');
  const thumbnails = gallery.querySelectorAll('.thumbnail-item');
  
  thumbnails.forEach(thumb => {
    thumb.addEventListener('click', () => {
      thumbnails.forEach(t => t.classList.remove('active'));
      thumb.classList.add('active');
      const newSrc = thumb.getAttribute('data-image-src');
      if (mainImage && newSrc) {
        mainImage.setAttribute('src', newSrc);
      }
    });
  });

  // Size Selector
  const sizeOptions = document.querySelectorAll('.size-option');
  sizeOptions.forEach(opt => {
    opt.addEventListener('click', () => {
      sizeOptions.forEach(o => o.classList.remove('active'));
      opt.classList.add('active');
      
      // Update Shopify hidden input
      const sizeInput = document.getElementById('selected-size-input');
      if (sizeInput) {
        sizeInput.value = opt.getAttribute('data-value');
      }
    });
  });
}

/* ==========================================================================
   CART & QUICK ADD ACTIONS (NATIVE SHOPIFY AJAX CART)
   ========================================================================== */
let cartData = { items: [] };
let isCartUpdating = false;

function formatMoney(cents) {
  const activeCurrency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'INR';
  const amount = (cents / 100).toFixed(2);
  const locale = activeCurrency === 'INR' ? 'en-IN' : 'en-US';
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: activeCurrency,
      minimumFractionDigits: 2
    }).format(amount);
  } catch (e) {
    return activeCurrency + ' ' + parseFloat(amount).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
}

function toggleCartLoading(loading) {
  isCartUpdating = loading;
  const drawer = document.getElementById('cart-drawer');
  if (drawer) {
    if (loading) {
      drawer.classList.add('drawer-loading');
    } else {
      drawer.classList.remove('drawer-loading');
    }
  }
  
  // Disable/enable add-to-cart and quick add buttons
  document.querySelectorAll('.pdp-add-btn, .quick-add-submit').forEach(btn => {
    btn.disabled = loading;
    if (loading) {
      btn.style.opacity = '0.5';
      btn.style.pointerEvents = 'none';
    } else {
      btn.style.opacity = '';
      btn.style.pointerEvents = '';
    }
  });

  // Disable/enable qty controllers inside the drawer
  document.querySelectorAll('.qty-btn, .cart-remove-btn').forEach(btn => {
    btn.disabled = loading;
    if (loading) {
      btn.style.opacity = '0.5';
      btn.style.pointerEvents = 'none';
    } else {
      btn.style.opacity = '';
      btn.style.pointerEvents = '';
    }
  });
}

function fetchCart() {
  return fetch('/cart.js')
    .then(response => {
      if (!response.ok) throw new Error('Network response was not ok');
      return response.json();
    })
    .then(cart => {
      cartData = cart;
      updateCartDrawerUI(cart);
      return cart;
    })
    .catch(error => {
      console.error('Error fetching cart:', error);
    });
}

function addToCart(variantId, qty = 1) {
  if (isCartUpdating) return;
  toggleCartLoading(true);

  let formData = {
    'items': [{
      'id': variantId,
      'quantity': qty
    }]
  };
  
  fetch('/cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(formData)
  })
  .then(response => {
    if (!response.ok) throw new Error('Network response was not ok');
    return response.json();
  })
  .then(item => {
    return fetchCart();
  })
  .then(() => {
    // Open cart drawer
    const cartOpenBtn = document.querySelector('.cart-open-btn');
    if (cartOpenBtn) cartOpenBtn.click();
  })
  .catch((error) => {
    console.error('Error adding to cart:', error);
  })
  .finally(() => {
    toggleCartLoading(false);
  });
}

function initQuickAdd() {
  const quickAddButtons = document.querySelectorAll('.quick-add-submit');
  quickAddButtons.forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      if (isCartUpdating) return;
      const variantId = btn.getAttribute('data-variant-id');
      if (variantId) {
        addToCart(variantId, 1);
      }
    });
  });
}

function initNativeCart() {
  fetchCart();

  // Intercept PDP product form submission
  const productForm = document.getElementById('product-form');
  if (productForm) {
    productForm.addEventListener('submit', (e) => {
      e.preventDefault();
      if (isCartUpdating) return;
      const variantInput = productForm.querySelector('input[name="id"]');
      if (variantInput) {
        const variantId = variantInput.value;
        const qtyInput = productForm.querySelector('input[name="quantity"]');
        const quantity = qtyInput ? parseInt(qtyInput.value) : 1;
        addToCart(variantId, quantity);
      }
    });
  }
}

function updateCartDrawerUI(cart) {
  const itemsContainer = document.querySelector('.cart-items-wrapper');
  const subtotalVal = document.querySelector('.subtotal-val');
  const cartCounts = document.querySelectorAll('.cart-count');
  
  if (!itemsContainer) return;
  
  if (!cart || !cart.items || cart.items.length === 0) {
    itemsContainer.innerHTML = '<p style="text-align:center; padding: 40px 0; color:#777;">Your cart is empty.</p>';
    if (subtotalVal) subtotalVal.textContent = formatMoney(0);
    cartCounts.forEach(c => c.textContent = '0');
    return;
  }
  
  let itemsHtml = '';
  let subtotal = cart.total_price;
  let totalCount = cart.item_count;
  
  cart.items.forEach((item) => {
    const itemImage = item.image || '';
    itemsHtml += `
      <div class="cart-item" data-id="${item.variant_id}">
        <div class="cart-item-image">
          <img src="${itemImage}" alt="${item.product_title || item.title}">
        </div>
        <div class="cart-item-details">
          <h4 class="cart-item-title">${item.product_title || item.title}</h4>
          ${item.variant_title ? `<p class="cart-item-variant" style="font-size: 10px; color: #777; text-transform: uppercase; margin-top: 2px;">${item.variant_title}</p>` : ''}
          <p class="cart-item-meta">${formatMoney(item.price)}</p>
          <div class="cart-item-actions">
            <div class="quantity-controller">
              <button class="qty-btn minus" onclick="changeQty('${item.variant_id}', -1)">-</button>
              <span class="qty-val">${item.quantity}</span>
              <button class="qty-btn plus" onclick="changeQty('${item.variant_id}', 1)">+</button>
            </div>
            <button class="cart-remove-btn" onclick="removeCartItem('${item.variant_id}')">Remove</button>
          </div>
        </div>
      </div>
    `;
  });
  
  itemsContainer.innerHTML = itemsHtml;
  if (subtotalVal) {
    subtotalVal.textContent = formatMoney(subtotal);
  }
  cartCounts.forEach(c => c.textContent = totalCount.toString());
}

// Global functions for cart interaction
window.changeQty = function(id, amount) {
  if (isCartUpdating) return;
  const item = cartData.items.find(item => item.variant_id == id || item.id == id);
  if (item) {
    toggleCartLoading(true);
    const newQty = item.quantity + amount;
    fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        id: String(id),
        quantity: newQty
      })
    })
    .then(response => {
      if (!response.ok) throw new Error('Network response was not ok');
      return response.json();
    })
    .then(cart => {
      cartData = cart;
      updateCartDrawerUI(cart);
    })
    .catch(error => {
      console.error('Error changing qty:', error);
    })
    .finally(() => {
      toggleCartLoading(false);
    });
  }
};

window.removeCartItem = function(id) {
  if (isCartUpdating) return;
  toggleCartLoading(true);
  fetch('/cart/change.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      id: String(id),
      quantity: 0
    })
  })
  .then(response => {
    if (!response.ok) throw new Error('Network response was not ok');
    return response.json();
  })
  .then(cart => {
    cartData = cart;
    updateCartDrawerUI(cart);
  })
  .catch(error => {
    console.error('Error removing item:', error);
  })
  .finally(() => {
    toggleCartLoading(false);
  });
};

window.addToCart = addToCart;


/* ==========================================================================
   PAGE PRELOADER
   ========================================================================== */
function initPreloader() {
  const preloader = document.getElementById('site-preloader');
  if (!preloader) return;

  const dismiss = () => {
    preloader.classList.add('hidden');
    document.body.style.overflow = '';
  };

  document.body.style.overflow = 'hidden';

  if (document.readyState === 'complete') {
    setTimeout(dismiss, 600);
  } else {
    window.addEventListener('load', () => setTimeout(dismiss, 600));
  }
  // Safety fallback — never blocks the user for more than 2.2s
  setTimeout(dismiss, 2200);
}

/* ==========================================================================
   SCROLL PROGRESS BAR
   ========================================================================== */
function initScrollProgress() {
  const bar = document.getElementById('scroll-progress-bar');
  if (!bar) return;

  window.addEventListener('scroll', () => {
    const scrollTop = window.scrollY;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    const pct = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
    bar.style.width = pct + '%';
  }, { passive: true });
}

/* ==========================================================================
   PRODUCT CARD SCROLL REVEAL (IntersectionObserver)
   ========================================================================== */
function initCardReveal() {
  const cards = document.querySelectorAll('.product-card');
  if (!cards.length) return;

  // Respect prefers-reduced-motion
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    cards.forEach(c => c.classList.add('card-visible'));
    return;
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('card-visible');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.08, rootMargin: '0px 0px -30px 0px' });

  cards.forEach(card => observer.observe(card));
}

/* ==========================================================================
   GENERAL SCROLL-REVEAL (.reveal-up elements)
   ========================================================================== */
function initRevealElements() {
  const els = document.querySelectorAll('.reveal-up');
  if (!els.length) return;

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    els.forEach(el => el.classList.add('is-visible'));
    return;
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.1 });

  els.forEach(el => observer.observe(el));
}

/* ==========================================================================
   MARK ACTIVE NAV LINK
   ========================================================================== */
function markActiveNavLink() {
  const path = window.location.pathname;
  document.querySelectorAll('.nav-link').forEach(link => {
    if (link.getAttribute('href') === path) {
      link.classList.add('active');
    }
  });
}

/* ==========================================================================
   IMAGE PROTECTION (Anti-download measures)
   ========================================================================== */
function initImageProtection() {
  // Prevent contextmenu (right-click) on all images
  document.addEventListener('contextmenu', (e) => {
    if (e.target.tagName === 'IMG' || (e.target.classList && e.target.classList.contains('product-card-image'))) {
      e.preventDefault();
    }
  }, false);

  // Prevent drag and drop on all images
  document.addEventListener('dragstart', (e) => {
    if (e.target.tagName === 'IMG' || (e.target.classList && e.target.classList.contains('product-card-image'))) {
      e.preventDefault();
    }
  }, false);
}

/* ==========================================================================
   IMAGE ZOOM DEGRADATION PROTECTION
   ========================================================================== */
function initZoomProtection() {
  const config = {
    threshold: 1.35,             // Scale above which degradation is active (1.35 = 135% zoom)
    restoreThreshold: 1.30,      // Scale below which original resolution is restored (1.30 = 130% zoom)
    method: 'combined',          // 'blur', 'swap', 'pixelate', or 'combined'
    blurAmount: '15px',          // CSS blur filter amount
    swapSize: '100x100',         // Low-res Shopify thumbnail size
    overlayText: 'Details Protected', // Warning overlay text
    targetSelectors: 'img'       // Target all images globally
  };

  let initialRatio = window.initialPixelRatio || window.devicePixelRatio || 1;
  let isDegraded = false;
  
  // Track all target images and their original attributes
  let targetImages = [];

  function updateTargets() {
    const allImages = Array.from(document.querySelectorAll(config.targetSelectors));
    
    // Filter out brand logos, preloaders, small icons and payment badges
    targetImages = allImages.filter(img => {
      // 1. Exclude preloader elements
      if (img.classList.contains('preloader-logo') || img.closest('#site-preloader') || img.closest('#site-preloader *')) {
        return false;
      }
      
      // 2. Exclude primary site brand logos
      if (img.closest('.logo') || img.closest('.header-logo') || img.closest('.footer-logo')) {
        return false;
      }
      
      // 3. Exclude small UI icons/badges (e.g. social, payment options, cart icons)
      const rect = img.getBoundingClientRect();
      const w = rect.width || img.width || 0;
      const h = rect.height || img.height || 0;
      
      const isTooSmall = (w > 0 && w < 60) || (h > 0 && h < 60);
      const isNaturalTooSmall = (img.naturalWidth > 0 && img.naturalWidth < 60) || (img.naturalHeight > 0 && img.naturalHeight < 60);
      
      if (isTooSmall || isNaturalTooSmall) {
        return false;
      }
      
      return true;
    });
    
    // Inject overlay container dynamically and adjust parent positioning
    targetImages.forEach(img => {
      const parent = img.parentElement;
      if (parent) {
        // Guarantee relative positioning on the parent container so overlay centers properly
        const computedStyle = window.getComputedStyle(parent);
        if (computedStyle.position === 'static') {
          parent.style.position = 'relative';
        }
        
        if (!parent.querySelector('.zoom-protection-overlay')) {
          const overlay = document.createElement('div');
          overlay.className = 'zoom-protection-overlay';
          overlay.textContent = config.overlayText;
          parent.appendChild(overlay);
        }
      }
      
      // Attempt to set crossOrigin to anonymous dynamically so that canvas pixelation can bypass CORS
      if (!img.crossOrigin && img.src && img.src.includes('cdn.shopify.com')) {
        img.crossOrigin = 'anonymous';
      }
    });
  }

  // Shopify image size converter helper
  function getShopifyLowResUrl(originalUrl, size) {
    if (!originalUrl) return '';
    const sizeRegex = /_(master|pico|icon|thumb|small|compact|medium|large|grand|1024x1024|2048x2048|(\d+x\d*)|(\d*x\d+))/gi;
    if (sizeRegex.test(originalUrl)) {
      return originalUrl.replace(sizeRegex, `_${size}`);
    }
    const pathParts = originalUrl.split('?');
    const baseUrl = pathParts[0];
    const query = pathParts[1] ? `?${pathParts[1]}` : '';
    const lastDotIndex = baseUrl.lastIndexOf('.');
    if (lastDotIndex === -1) return originalUrl;
    const name = baseUrl.substring(0, lastDotIndex);
    const ext = baseUrl.substring(lastDotIndex);
    return `${name}_${size}${ext}${query}`;
  }

  // Canvas offline pixelation helper
  function getPixelatedDataUrl(img, scale = 0.03) {
    if (!img || !img.complete || img.naturalWidth === 0) return null;
    if (img._pixelatedDataUrl) return img._pixelatedDataUrl;

    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      canvas.width = w;
      canvas.height = h;

      // Draw the image at a microscopic size onto a temporary canvas
      const sw = Math.max(1, Math.round(w * scale));
      const sh = Math.max(1, Math.round(h * scale));
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = sw;
      tempCanvas.height = sh;
      const tempCtx = tempCanvas.getContext('2d');
      tempCtx.drawImage(img, 0, 0, sw, sh);

      // Disable image smoothing for extreme pixel blocks
      ctx.imageSmoothingEnabled = false;
      ctx.mozImageSmoothingEnabled = false;
      ctx.webkitImageSmoothingEnabled = false;
      ctx.msImageSmoothingEnabled = false;

      // Scale back up to full size
      ctx.drawImage(tempCanvas, 0, 0, sw, sh, 0, 0, w, h);
      
      // Cache data URL
      img._pixelatedDataUrl = canvas.toDataURL('image/jpeg', 0.6);
      return img._pixelatedDataUrl;
    } catch (e) {
      console.warn('Canvas pixelation failed (CORS restriction):', e);
      return null;
    }
  }

  // Detect scale
  function getZoomScale() {
    let visualScale = 1.0;
    if (window.visualViewport) {
      visualScale = window.visualViewport.scale;
    }

    let desktopScale = 1.0;
    const currentRatio = window.devicePixelRatio || 1;
    if (currentRatio && initialRatio) {
      desktopScale = currentRatio / initialRatio;
    }

    return Math.max(visualScale, desktopScale);
  }

  // Degrade target images
  function degradeImages() {
    if (isDegraded) return;
    isDegraded = true;

    targetImages.forEach(img => {
      const parent = img.parentElement;
      if (parent) {
        parent.classList.add('zoom-protection-active');
      }

      // Save original attributes
      if (!img.getAttribute('data-original-src')) {
        img.setAttribute('data-original-src', img.src);
      }
      
      const srcset = img.getAttribute('srcset');
      if (srcset && srcset !== '') {
        img.setAttribute('data-original-srcset', srcset);
        img.removeAttribute('srcset'); // Remove to prevent loading high-res alternatives
      }

      const sizes = img.getAttribute('sizes');
      if (sizes && sizes !== '') {
        img.setAttribute('data-original-sizes', sizes);
        img.removeAttribute('sizes');
      }

      // Apply blur class
      if (config.method === 'blur' || config.method === 'combined') {
        img.classList.add('zoom-degraded');
      }

      // Apply source swapping
      if (config.method === 'swap' || config.method === 'pixelate' || config.method === 'combined') {
        const originalSrc = img.getAttribute('data-original-src');
        let newSrc = '';

        if (config.method === 'pixelate' || config.method === 'combined') {
          newSrc = getPixelatedDataUrl(img);
        }

        // Fallback to Shopify low-res thumbnail if pixelation failed or was not chosen
        if (!newSrc) {
          newSrc = getShopifyLowResUrl(originalSrc, config.swapSize);
        }

        if (newSrc && img.src !== newSrc) {
          img.src = newSrc;
        }
      }
    });
  }

  // Restore target images
  function restoreImages() {
    if (!isDegraded) return;
    isDegraded = false;

    targetImages.forEach(img => {
      const parent = img.parentElement;
      if (parent) {
        parent.classList.remove('zoom-protection-active');
      }

      const originalSrc = img.getAttribute('data-original-src');
      const originalSrcset = img.getAttribute('data-original-srcset');
      const originalSizes = img.getAttribute('data-original-sizes');

      if (originalSrc) {
        img.src = originalSrc;
        img.removeAttribute('data-original-src');
      }

      if (originalSrcset) {
        img.setAttribute('srcset', originalSrcset);
        img.removeAttribute('data-original-srcset');
      }

      if (originalSizes) {
        img.setAttribute('sizes', originalSizes);
        img.removeAttribute('data-original-sizes');
      }

      img.classList.remove('zoom-degraded');
    });
  }

  function checkZoom() {
    const currentScale = getZoomScale();
    if (currentScale > config.threshold) {
      degradeImages();
    } else if (currentScale <= config.restoreThreshold) {
      restoreImages();
    }
  }

  // Throttle using requestAnimationFrame
  let isChecking = false;
  function triggerCheck() {
    if (!isChecking) {
      isChecking = true;
      requestAnimationFrame(() => {
        checkZoom();
        isChecking = false;
      });
    }
  }

  // Listeners
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', triggerCheck);
    window.visualViewport.addEventListener('scroll', triggerCheck);
  }
  window.addEventListener('resize', triggerCheck);

  // Initial check and set up periodic scanner to capture dynamically loaded images (e.g. variant switches)
  updateTargets();
  checkZoom();

  // Monitor DOM modifications (variant switch, ajax loading, etc.) to capture new images
  const observer = new MutationObserver(() => {
    updateTargets();
    checkZoom();
  });
  
  // Observe the entire document body for dynamic changes (lazy loading, infinite scroll, page changes)
  observer.observe(document.body, { childList: true, subtree: true });

  // Reset baseline when screen changes (e.g. moving between monitors)
  window.addEventListener('focus', () => {
    initialRatio = window.devicePixelRatio || 1;
  });
}

/* ==========================================================================
   PREDICTIVE SEARCH
   ========================================================================== */
function initPredictiveSearch() {
  const searchInput = document.querySelector('.search-bar-input');
  const suggestionsContainer = document.querySelector('.search-suggestions');
  const resultsContainer = document.querySelector('.predictive-search-results-container');
  const resultsContent = document.querySelector('.predictive-search-results');
  const loadingIndicator = document.querySelector('.predictive-search-loading');

  if (!searchInput || !resultsContainer || !resultsContent) return;

  let debounceTimer;

  // Listen for search input typing
  searchInput.addEventListener('input', () => {
    clearTimeout(debounceTimer);
    const query = searchInput.value.trim();

    if (query.length === 0) {
      showSuggestions();
      return;
    }

    debounceTimer = setTimeout(() => {
      fetchPredictiveSearchResults(query);
    }, 300);
  });

  function showSuggestions() {
    if (suggestionsContainer) suggestionsContainer.style.display = 'grid';
    resultsContainer.style.display = 'none';
    resultsContent.innerHTML = '';
  }

  function showLoading() {
    if (suggestionsContainer) suggestionsContainer.style.display = 'none';
    resultsContainer.style.display = 'block';
    if (loadingIndicator) loadingIndicator.style.display = 'flex';
    resultsContent.style.opacity = '0.5';
  }

  function hideLoading() {
    if (loadingIndicator) loadingIndicator.style.display = 'none';
    resultsContent.style.opacity = '1';
  }

  async function fetchPredictiveSearchResults(query) {
    showLoading();

    try {
      const response = await fetch(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,collection,page,article&resources[limit]=6`);
      if (!response.ok) {
        throw new Error('Predictive search network response not ok');
      }
      const data = await response.json();
      renderSearchResults(data, query);
    } catch (error) {
      console.error('Error fetching predictive search results:', error);
      renderSearchError();
    } finally {
      hideLoading();
    }
  }

  function renderSearchResults(data, query) {
    const products = data.resources.results.products || [];
    const collections = data.resources.results.collections || [];
    const pages = data.resources.results.pages || [];
    const articles = data.resources.results.articles || [];

    const hasProducts = products.length > 0;
    const hasOthers = collections.length > 0 || pages.length > 0 || articles.length > 0;

    if (!hasProducts && !hasOthers) {
      resultsContent.innerHTML = `
        <div class="predictive-search-empty">
          <p class="predictive-search-empty-text">No results found for "${escapeHTML(query)}"</p>
          <p style="font-size: 13px; color: var(--color-text-light);">Try checking your spelling or search for another term.</p>
        </div>
      `;
      return;
    }

    const gridClass = `predictive-search-grid${!hasOthers ? ' no-others' : ''}${!hasProducts ? ' no-products' : ''}`;
    let html = `<div class="${gridClass}">`;

    // Products Column
    if (hasProducts) {
      const desktopCols = hasOthers ? 3 : 4;
      html += '<div class="predictive-search-column-products">';
      html += '<h4 class="predictive-search-group-title">Products</h4>';
      html += `<div class="product-grid" style="--desktop-cols: ${desktopCols};">`;
      
      products.forEach(product => {
        const title = escapeHTML(product.title);
        const url = product.url;
        const image = product.image ? product.image : '';
        
        // Price On Request logic: identified if price is 0, invalid, or >= 2,75,000 INR (unless tagged allow-checkout/enable-checkout)
        const cleanPrice = parseFloat(product.price.replace(/[^\d.]/g, ''));
        const productTags = Array.isArray(product.tags) ? product.tags : (typeof product.tags === 'string' ? product.tags.split(',') : []);
        const allowCheckout = productTags.some(t => t.trim() === 'allow-checkout' || t.trim() === 'enable-checkout');
        const isPor = !allowCheckout && (isNaN(cleanPrice) || cleanPrice === 0 || cleanPrice >= 275000);
        
        const price = isPor ? 'Price on Request' : formatPrice(product.price);
        const comparePrice = product.compare_at_price ? formatPrice(product.compare_at_price) : '';
        const onSale = !isPor && product.compare_at_price && parseFloat(product.compare_at_price) > parseFloat(product.price);

        html += `
          <div class="product-card card-visible" data-product-id="${product.id}">
            <div class="product-card-image-wrapper">
              <a href="${url}">
                ${image 
                  ? `<img class="product-card-image primary" src="${image}" alt="${title}" loading="lazy">` 
                  : `<div style="background-color: var(--color-secondary); height: 100%; display: flex; align-items: center; justify-content: center;"><span style="font-size:11px; text-transform:uppercase; color:var(--color-text-light);">No Image</span></div>`
                }
              </a>
              
              ${!isPor ? `
                <button class="product-card-quick-add quick-add-submit" 
                        data-product-handle="${product.handle}"
                        data-variant-id="" 
                        data-title="${title}" 
                        data-price="${price}" 
                        data-image="${image}">
                  Quick Add
                </button>
              ` : ''}
            </div>
            
            <div class="product-card-info">
              <a href="${url}" class="product-card-title">${title}</a>
              ${isPor ? `
                <span class="product-card-price">Price on Request</span>
              ` : `
                <div class="product-card-price-wrapper">
                  ${onSale ? `
                    <span class="product-card-price compare-at">${comparePrice}</span>
                    <span class="product-card-price sale">${price}</span>
                  ` : `
                    <span class="product-card-price">${price}</span>
                  `}
                </div>
              `}
            </div>
          </div>
        `;
      });

      html += '</div></div>'; // Close product-grid & products column
    } else {
      html += '<div></div>'; // Spacing column
    }

    // Others Column (Collections, Pages, Articles)
    if (hasOthers) {
      html += '<div class="predictive-search-column-others">';

      if (collections.length > 0) {
        html += '<div class="predictive-search-group">';
        html += '<h4 class="predictive-search-group-title">Collections</h4>';
        html += '<ul class="predictive-search-list">';
        collections.forEach(collection => {
          html += `
            <li class="predictive-search-list-item">
              <a href="${collection.url}">
                <span>${escapeHTML(collection.title)}</span>
                <span class="predictive-search-list-item-arrow">&rarr;</span>
              </a>
            </li>
          `;
        });
        html += '</ul></div>';
      }

      if (pages.length > 0) {
        html += '<div class="predictive-search-group">';
        html += '<h4 class="predictive-search-group-title">Pages</h4>';
        html += '<ul class="predictive-search-list">';
        pages.forEach(page => {
          html += `
            <li class="predictive-search-list-item">
              <a href="${page.url}">
                <span>${escapeHTML(page.title)}</span>
                <span class="predictive-search-list-item-arrow">&rarr;</span>
              </a>
            </li>
          `;
        });
        html += '</ul></div>';
      }

      if (articles.length > 0) {
        html += '<div class="predictive-search-group">';
        html += '<h4 class="predictive-search-group-title">Journal</h4>';
        html += '<ul class="predictive-search-list">';
        articles.forEach(article => {
          html += `
            <li class="predictive-search-list-item">
              <a href="${article.url}">
                <span>${escapeHTML(article.title)}</span>
                <span class="predictive-search-list-item-arrow">&rarr;</span>
              </a>
            </li>
          `;
        });
        html += '</ul></div>';
      }

      html += '</div>'; // Close others column
    }

    html += '</div>'; // Close grid

    // View All Footer
    html += `
      <div class="predictive-search-view-all">
        <a href="/search?q=${encodeURIComponent(query)}" class="predictive-search-view-all-btn">
          View all results for "${escapeHTML(query)}"
        </a>
      </div>
    `;

    resultsContent.innerHTML = html;

    // Attach click events to dynamically rendered Quick Add buttons
    resultsContent.querySelectorAll('.quick-add-submit').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (btn.classList.contains('quick-add-loading')) return;
        
        const handle = btn.getAttribute('data-product-handle');
        if (!handle) return;
        
        btn.classList.add('quick-add-loading');
        const originalText = btn.textContent;
        btn.textContent = 'Adding...';
        btn.disabled = true;
        
        try {
          const res = await fetch(`/products/${handle}.js`);
          if (!res.ok) throw new Error('Product details fetch failed');
          const productJson = await res.json();
          const firstAvailableVariant = productJson.variants.find(v => v.available) || productJson.variants[0];
          if (firstAvailableVariant) {
            if (typeof addToCart === 'function') {
              addToCart(firstAvailableVariant.id, 1);
            }
          }
        } catch (err) {
          console.error('Error quick adding product in search:', err);
        } finally {
          btn.classList.remove('quick-add-loading');
          btn.textContent = originalText;
          btn.disabled = false;
        }
      });
    });
  }

  function renderSearchError() {
    resultsContent.innerHTML = `
      <div class="predictive-search-empty">
        <p class="predictive-search-empty-text">An error occurred while searching</p>
        <p style="font-size: 13px; color: var(--color-text-light);">Please try again.</p>
      </div>
    `;
  }

  // Price Formatter Helper
  function formatPrice(priceString) {
    if (!priceString) return '';
    if (priceString.includes('₹') || priceString.includes('Rs') || priceString.includes('$') || priceString.includes('€') || priceString.includes('£')) {
      return priceString;
    }
    const val = parseFloat(priceString);
    if (isNaN(val)) return priceString;
    
    const activeCurrency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'INR';
    const locale = activeCurrency === 'INR' ? 'en-IN' : 'en-US';
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: activeCurrency,
        minimumFractionDigits: 2
      }).format(val);
    } catch (e) {
      return activeCurrency + ' ' + val.toFixed(2);
    }
  }

  // HTML escaping utility to prevent XSS
  function escapeHTML(str) {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
}

/* ==========================================================================
   SIZE GUIDE MODAL
   ========================================================================== */
function initSizeGuide() {
  const modal = document.getElementById('size-guide-modal');
  if (!modal) return;

  const openButtons = document.querySelectorAll('[data-open-size-guide], .size-guide-trigger, .pdp-size-guide-btn');
  const closeButtons = modal.querySelectorAll('[data-close-size-guide]');
  const unitPills = modal.querySelectorAll('.unit-pill');
  const table = modal.querySelector('.size-guide-table');
  const tabs = modal.querySelectorAll('.size-guide-tab');
  const panels = modal.querySelectorAll('.size-guide-panel');

  function openModal(e) {
    if (e) e.preventDefault();
    if (typeof modal.showModal === 'function') {
      modal.showModal();
    } else {
      modal.setAttribute('open', '');
    }
    document.body.style.overflow = 'hidden';
  }

  function closeModal() {
    if (typeof modal.close === 'function') {
      modal.close();
    } else {
      modal.removeAttribute('open');
    }
    document.body.style.overflow = '';
  }

  openButtons.forEach(btn => {
    btn.addEventListener('click', openModal);
  });

  // Event delegation so dynamically rendered or cloned buttons also work
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-open-size-guide], .size-guide-trigger, .pdp-size-guide-btn');
    if (trigger) {
      e.preventDefault();
      openModal();
    }
  });

  closeButtons.forEach(btn => {
    btn.addEventListener('click', closeModal);
  });

  modal.addEventListener('close', () => {
    document.body.style.overflow = '';
  });

  modal.addEventListener('cancel', () => {
    document.body.style.overflow = '';
  });

  // Light-dismiss fallback for browsers without native closedby support
  if (!('closedBy' in HTMLDialogElement.prototype)) {
    modal.addEventListener('click', (event) => {
      if (event.target !== modal) return;
      const inner = modal.querySelector('.size-guide-dialog-inner');
      if (!inner) return;
      const rect = inner.getBoundingClientRect();
      const isInside = (
        rect.top <= event.clientY &&
        event.clientY <= rect.top + rect.height &&
        rect.left <= event.clientX &&
        event.clientX <= rect.left + rect.width
      );
      if (!isInside) {
        closeModal();
      }
    });
  }

  // Unit Toggle (Inches <-> CM)
  unitPills.forEach(pill => {
    pill.addEventListener('click', () => {
      const unit = pill.getAttribute('data-unit');
      unitPills.forEach(p => {
        p.classList.toggle('active', p === pill);
        p.setAttribute('aria-pressed', p === pill ? 'true' : 'false');
      });
      if (table) {
        table.setAttribute('data-active-unit', unit);
      }
    });
  });

  // Tab switching (Size Chart <-> How to Measure)
  tabs.forEach(tab => {
    tab.addEventListener('click', () => {
      const target = tab.getAttribute('data-tab-target');
      tabs.forEach(t => {
        const isActive = t === tab;
        t.classList.toggle('active', isActive);
        t.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
      panels.forEach(p => {
        if (p.id === `size-guide-panel-${target}`) {
          p.classList.add('active');
          p.removeAttribute('hidden');
        } else {
          p.classList.remove('active');
          p.setAttribute('hidden', '');
        }
      });
    });
  });
}


